// Feedback Review, layout B "side rail" (2026-10-26, docs/feedback.md).
//
// Each card: a header row (contact number, date, intent, company · channel,
// the resolved order, who sent it; Open in Agent View and Activity log on
// the right), then two columns that wrap to one on a narrow screen: on the
// left the captured screenshot (a preview, aligned right; 2026-10-27), the
// CSR's feedback (4 lines, Show more) and the triage; a rail on the right
// (420px basis, at most 480px) with Priority, Assign to, the Resolution
// note and Save note / Mark complete. Completed items use the same card,
// muted, read-only. The page adds Intent and Group filters, a Groups view
// and a Daily digest panel (collapsed by default).
import { useEffect, useLayoutEffect, useState, useCallback, useMemo, useRef } from 'react'
import {
  Box, Stack, Typography, Chip, Button, TextField, Select, MenuItem,
  FormControl, InputLabel, FormControlLabel, Switch, Paper, CircularProgress,
  Alert, Divider, Link, ToggleButton, ToggleButtonGroup,
  Accordion, AccordionSummary, AccordionDetails,
} from '@mui/material'
import { alpha } from '@mui/material/styles'
import {
  fetchFeedback, updateFeedbackItem, fetchFeedbackUsers, fetchFeedbackScreenshotObjectUrl,
  fetchFeedbackGroups, fetchFeedbackDigest, retryFeedbackTriage,
} from '../../api.js'
import { intentLabel, INTENT_COLOR } from '../../constants/intents.js'
import { KNOWN_CHANNELS } from '../../constants/channels.js'
import { KNOWN_COMPANIES } from '../../constants/companies.js'
import ImageLightbox from '../../ImageLightbox.jsx'
// "As the CSR saw it" (2026-10-29): the report's screen state, read-only.
import FeedbackScreenView from '../../FeedbackScreenView.jsx'
import { convNumberPath } from '../../contactPath.js'
import {
  FEEDBACK_LINE_HEIGHT_PX, FEEDBACK_COLLAPSED_HEIGHT_PX, formatFeedbackDate, formatCompletedDate,
  activityLogPath, triageView, similarText, filterFeedback, groupOptions, mergeUpdated, UNASSIGNED_FILTER_VALUE,
  screenshotSlot, RAIL_BASIS_PX, RAIL_MAX_WIDTH_PX,
} from './feedbackReview.js'

const PRIORITY_OPTIONS = ['high', 'medium', 'low']

// Unset priority sorts last — matches the server's own SQL CASE statement.
function priorityRank(priority) {
  const idx = PRIORITY_OPTIONS.indexOf(priority)
  return idx === -1 ? PRIORITY_OPTIONS.length : idx
}

const byPriority = (a, b) => priorityRank(a.priority) - priorityRank(b.priority)
const byCreatedDesc = (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
const byCreatedAsc = (a, b) => new Date(a.createdAt) - new Date(b.createdAt)
const byResolvedDesc = (a, b) => new Date(b.completedAt ?? 0) - new Date(a.completedAt ?? 0)
const byResolvedAsc = (a, b) => new Date(a.completedAt ?? 0) - new Date(b.completedAt ?? 0)

const NEW_SORT_COMPARATORS = { priority: byPriority, created_desc: byCreatedDesc, created_asc: byCreatedAsc }
const DONE_SORT_COMPARATORS = { resolved_desc: byResolvedDesc, resolved_asc: byResolvedAsc, priority: byPriority }

const SCREENSHOT_PREVIEW_WIDTH_PX = 240
const SCREENSHOT_PREVIEW_MAX_HEIGHT_PX = 180
// The lightbox shows the object URL as is (no CDN upsizing).
const identityUrl = (u) => u

const LABEL_SX = { fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' }

function ExpandIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><path d="m6 9 6 6 6-6" /></svg>
  )
}

// One captured screenshot, as a preview about 240px wide (max 180px tall).
// The bytes are only reachable through the admin-only GET .../screenshot
// route (never a direct blob URL), so this fetches its own object URL, and
// only once the card is near the viewport. The URL is revoked on unmount.
function FeedbackScreenshotPreview({ feedbackId, onLoaded, onOpen }) {
  const ref = useRef(null)
  const [near, setNear] = useState(false)
  const [url, setUrl] = useState(null)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    if (near) return undefined
    const el = ref.current
    if (!el || typeof IntersectionObserver === 'undefined') { setNear(true); return undefined }
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) { setNear(true); observer.disconnect() }
    }, { rootMargin: '300px 0px' })
    observer.observe(el)
    return () => observer.disconnect()
  }, [near])

  useEffect(() => {
    if (!near) return undefined
    let cancelled = false
    let objectUrl = null
    fetchFeedbackScreenshotObjectUrl(feedbackId)
      .then((u) => {
        if (cancelled) { URL.revokeObjectURL(u); return }
        objectUrl = u
        setUrl(u)
        onLoaded(feedbackId, u)
      })
      .catch(() => { if (!cancelled) setFailed(true) })
    return () => {
      cancelled = true
      if (objectUrl) URL.revokeObjectURL(objectUrl)
      onLoaded(feedbackId, null)
    }
  }, [near, feedbackId, onLoaded])

  if (failed) return null
  if (!url) return <Box ref={ref} data-testid="feedback-screenshot-placeholder" sx={{ width: SCREENSHOT_PREVIEW_WIDTH_PX, height: 135, flexShrink: 0, borderRadius: 1, border: 1, borderColor: 'divider' }} />
  return (
    <Box
      component="img"
      src={url}
      alt="Screenshot"
      data-testid="feedback-screenshot"
      role="button"
      tabIndex={0}
      onClick={() => onOpen(feedbackId)}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen(feedbackId) } }}
      sx={{
        display: 'block', width: SCREENSHOT_PREVIEW_WIDTH_PX, height: 'auto', maxHeight: SCREENSHOT_PREVIEW_MAX_HEIGHT_PX,
        objectFit: 'contain', objectPosition: 'right top', flexShrink: 0,
        borderRadius: 1, border: 1, borderColor: 'divider', cursor: 'zoom-in',
      }}
    />
  )
}

// The row directly above FEEDBACK, aligned right: the screenshot preview(s),
// side by side when there is more than one, or the muted text saying why
// there is none. A click opens the image lightbox.
function ScreenshotRow({ item }) {
  const slot = screenshotSlot(item)
  const [urls, setUrls] = useState({})
  const [openIndex, setOpenIndex] = useState(null)
  const onLoaded = useCallback((id, url) => setUrls((prev) => {
    if (url) return { ...prev, [id]: url }
    if (!(id in prev)) return prev
    const next = { ...prev }
    delete next[id]
    return next
  }), [])
  // Kept stable across re-renders so an open lightbox keeps its zoom.
  const idsKey = slot?.kind === 'images' ? slot.ids.join('|') : ''
  const images = useMemo(() => (idsKey ? idsKey.split('|') : [])
    .filter((id) => urls[id]).map((id) => ({ id, url: urls[id], name: 'Screenshot' })), [idsKey, urls])
  const openPreview = (id) => {
    const i = images.findIndex((img) => img.id === id)
    if (i >= 0) setOpenIndex(i)
  }

  if (!slot) return null
  if (slot.kind === 'text') {
    return (
      <Stack direction="row" justifyContent="flex-end" data-testid="feedback-screenshot-row">
        <Typography variant="caption" color="text.secondary" fontStyle="italic" data-testid={slot.testId}>{slot.text}</Typography>
      </Stack>
    )
  }
  return (
    <Stack direction="row" spacing={1} justifyContent="flex-end" alignItems="flex-start" flexWrap="wrap" useFlexGap data-testid="feedback-screenshot-row">
      {slot.ids.map((id) => <FeedbackScreenshotPreview key={id} feedbackId={id} onLoaded={onLoaded} onOpen={openPreview} />)}
      {openIndex != null && (
        <ImageLightbox images={images} index={openIndex} onClose={() => setOpenIndex(null)} onNavigate={setOpenIndex} fullResUrl={identityUrl} />
      )}
    </Stack>
  )
}

// The CSR's text: 4 lines (21px), cut with no fade; Show more / Show less
// only when it is longer, measured on the rendered text.
function FeedbackText({ message }) {
  const ref = useRef(null)
  const [expanded, setExpanded] = useState(false)
  const [long, setLong] = useState(false)

  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return undefined
    const measure = () => setLong(el.scrollHeight > FEEDBACK_COLLAPSED_HEIGHT_PX + 1)
    measure()
    if (typeof ResizeObserver === 'undefined') return undefined
    const observer = new ResizeObserver(measure)
    observer.observe(el)
    return () => observer.disconnect()
  }, [message])

  return (
    <Stack spacing={0.5}>
      <Typography component="span" color="text.secondary" sx={LABEL_SX}>Feedback</Typography>
      <Typography
        ref={ref}
        data-testid="feedback-message"
        data-expanded={expanded ? 'true' : 'false'}
        sx={{
          m: 0, fontSize: 14, lineHeight: `${FEEDBACK_LINE_HEIGHT_PX}px`, whiteSpace: 'pre-line', wordBreak: 'break-word',
          ...(expanded ? {} : { maxHeight: FEEDBACK_COLLAPSED_HEIGHT_PX, overflow: 'hidden' }),
        }}
      >
        {message}
      </Typography>
      {(long || expanded) && (
        <Link component="button" type="button" underline="hover" data-testid="feedback-toggle"
          onClick={() => setExpanded((v) => !v)} sx={{ alignSelf: 'flex-start', fontSize: 13 }}>
          {expanded ? 'Show less' : 'Show more'}
        </Link>
      )}
    </Stack>
  )
}

function TriageBlock({ item, onRetry, onFilterGroup }) {
  const [retrying, setRetrying] = useState(false)
  const [retryError, setRetryError] = useState(null)
  const view = triageView(item.triage)
  const similar = similarText(item.groupCount14d)

  async function retry() {
    setRetrying(true); setRetryError(null)
    try { await onRetry(item.id) } catch (e) { setRetryError(e.message) } finally { setRetrying(false) }
  }

  return (
    <Box data-testid="feedback-triage" sx={{ bgcolor: (t) => alpha(t.palette.info.main, t.palette.mode === 'dark' ? 0.16 : 0.07), borderRadius: 1.5, px: 1.75, py: 1.5 }}>
      <Stack direction="row" spacing={1.25} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: view.state === 'ok' ? 0.75 : 0.25 }}>
        <Typography component="span" sx={{ ...LABEL_SX, color: 'info.dark' }}>Triage</Typography>
        {item.groupName && <Chip size="small" variant="outlined" label={item.groupName} data-testid="feedback-group-chip" sx={{ bgcolor: 'background.paper' }} />}
        {similar && item.groupKey && (
          <Link component="button" type="button" underline="hover" data-testid="feedback-similar"
            onClick={() => onFilterGroup(item.groupKey)} sx={{ fontSize: 12 }}>
            {similar}
          </Link>
        )}
      </Stack>
      {view.state === 'ok' ? (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '150px minmax(0, 1fr)' }, rowGap: 0.4, columnGap: 1.5, fontSize: 13, lineHeight: '19px' }}>
          {view.rows.map((r) => (
            <Box key={r.key} sx={{ display: 'contents' }}>
              <Typography component="span" color="text.secondary" sx={{ fontSize: 13, lineHeight: '19px' }}>{r.label}</Typography>
              <Typography component="span" data-testid={`feedback-triage-${r.key}`} sx={{ fontSize: r.key === 'where' ? 12 : 13, lineHeight: '19px', fontFamily: r.key === 'where' ? 'Consolas, monospace' : undefined, wordBreak: 'break-word' }}>{r.value}</Typography>
            </Box>
          ))}
        </Box>
      ) : (
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="body2" color="text.secondary" data-testid="feedback-triage-status">{view.text}</Typography>
          {view.state === 'failed' && (
            <Button size="small" onClick={retry} disabled={retrying} startIcon={retrying ? <CircularProgress size={12} color="inherit" /> : null}>Retry</Button>
          )}
        </Stack>
      )}
      {retryError && <Alert severity="error" sx={{ mt: 1 }} onClose={() => setRetryError(null)}>{retryError}</Alert>}
    </Box>
  )
}

function FeedbackCard({ item, users, onUpdated, onOpenInAgent, onRetryTriage, onFilterGroup }) {
  const completed = item.status === 'completed'
  const [resolution, setResolution] = useState(item.resolution ?? '')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [screenOpen, setScreenOpen] = useState(false)

  async function patch(data) {
    setSaving(true); setError(null)
    try {
      const updated = await updateFeedbackItem(item.id, data)
      onUpdated(updated)
    } catch (e) {
      setError(e.message)
    } finally {
      setSaving(false)
    }
  }

  const activityPath = activityLogPath(item.convNumber, item.id)
  const account = [item.company, item.channel].filter(Boolean).join(' · ')

  return (
    <Paper
      variant="outlined"
      data-testid={completed ? 'feedback-card-completed' : 'feedback-card-open'}
      sx={{
        px: 2.25, py: 2, borderRadius: 2,
        ...(completed ? { opacity: 0.75, bgcolor: (t) => alpha(t.palette.success.main, 0.04) } : {}),
      }}
    >
      <Stack spacing={1.5}>
        {/* Header row */}
        <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap data-testid="feedback-header">
          {item.convNumber != null ? (
            <Link href={convNumberPath(item.convNumber)} underline="hover" data-testid="feedback-contact-link"
              onClick={(e) => { e.preventDefault(); onOpenInAgent?.(item.conversationId, item.convNumber) }}
              sx={{ fontSize: 15, fontWeight: 700 }}>
              #{item.convNumber}
            </Link>
          ) : (
            <Typography component="span" sx={{ fontSize: 15, fontWeight: 700 }} color="text.secondary">No contact</Typography>
          )}
          <Typography component="span" variant="body2" color="text.secondary" data-testid="feedback-date">{formatFeedbackDate(item.createdAt)}</Typography>
          <Box component="span" sx={{ width: '1px', height: 16, bgcolor: 'divider' }} />
          {completed && <Chip size="small" color="success" label="completed" />}
          {item.intent && (
            <Chip size="small" color={INTENT_COLOR[item.intent] ?? 'default'} variant="outlined" label={intentLabel(item.intent)} />
          )}
          {account && <Typography component="span" variant="body2" color="text.secondary">{account}</Typography>}
          {item.orderNumber && (
            <Typography component="span" variant="body2" color="text.secondary" data-testid="feedback-order">
              Order <Box component="b" sx={{ color: 'text.primary', fontFamily: 'Consolas, monospace', fontWeight: 600 }}>{item.orderNumber}</Box>
            </Typography>
          )}
          <Typography component="span" variant="body2" color="text.secondary">
            From <Box component="b" sx={{ color: 'text.primary' }}>{item.submittedBy}</Box>
          </Typography>
          <Box sx={{ flexGrow: 1 }} />
          {item.convNumber != null && (
            <Link href="#" underline="hover" sx={{ fontSize: 13 }}
              onClick={(e) => { e.preventDefault(); onOpenInAgent?.(item.conversationId, item.convNumber) }}>
              Open in Agent View
            </Link>
          )}
          {/* "As the CSR saw it" (2026-10-29): only a report that carries
              its screen state; an older report shows no button. */}
          {item.hasScreenState && (
            <Button size="small" variant="outlined" onClick={() => setScreenOpen(true)} data-testid="feedback-view-screen"
              sx={{ textTransform: 'none', fontSize: 13, py: 0.25 }}>
              View screen
            </Button>
          )}
          {activityPath && (
            <Link href={activityPath} target="_blank" rel="noopener" underline="hover" sx={{ fontSize: 13 }} data-testid="feedback-activity-link">
              Activity log
            </Link>
          )}
        </Stack>
        {screenOpen && <FeedbackScreenView feedbackId={item.id} onClose={() => setScreenOpen(false)} />}

        {/* Body: the feedback and the triage | the side rail */}
        <Box sx={{ display: 'flex', gap: 2.5, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <Stack spacing={1.5} sx={{ flex: '999 1 560px', minWidth: 0 }}>
            <ScreenshotRow item={item} />
            <FeedbackText message={item.message} />
            <TriageBlock item={item} onRetry={onRetryTriage} onFilterGroup={onFilterGroup} />
          </Stack>

          <Stack spacing={1.25} data-testid="feedback-rail"
            sx={{ flex: `1 1 ${RAIL_BASIS_PX}px`, minWidth: 0, maxWidth: { xs: '100%', md: RAIL_MAX_WIDTH_PX }, pl: { xs: 0, md: 2 }, borderLeft: { xs: 0, md: 1 }, borderColor: { md: 'divider' } }}>
            <FormControl size="small" fullWidth>
              <InputLabel>Priority</InputLabel>
              <Select label="Priority" value={item.priority ?? ''} disabled={saving || completed}
                onChange={(e) => patch({ priority: e.target.value || null })}>
                <MenuItem value=""><em>None</em></MenuItem>
                {PRIORITY_OPTIONS.map((p) => (
                  <MenuItem key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" fullWidth>
              <InputLabel>Assign to</InputLabel>
              <Select label="Assign to" value={item.assigneeId ?? ''} disabled={saving || completed}
                onChange={(e) => {
                  const user = users.find((u) => u.id === e.target.value)
                  patch({ assigneeId: user?.id ?? null, assigneeName: user?.name ?? null })
                }}>
                <MenuItem value=""><em>Unassigned</em></MenuItem>
                {users.map((u) => (
                  <MenuItem key={u.id} value={u.id}>{u.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              size="small"
              label="Resolution note"
              value={completed ? (item.resolution ?? '') : resolution}
              onChange={(e) => setResolution(e.target.value)}
              multiline
              rows={4}
              fullWidth
              disabled={saving}
              placeholder={completed ? '' : 'Optional…'}
              InputProps={{ readOnly: completed }}
              inputProps={{ style: { resize: 'vertical' } }}
            />
            {completed ? (
              <Typography variant="body2" color="text.secondary" data-testid="feedback-completed-date">
                Completed {formatCompletedDate(item.completedAt ?? item.createdAt)}
              </Typography>
            ) : (
              <Stack direction="row" spacing={1}>
                <Button size="small" variant="outlined" sx={{ flex: 1 }} disabled={saving}
                  startIcon={saving ? <CircularProgress size={14} color="inherit" /> : null}
                  onClick={() => patch({ resolution: resolution.trim() || null })}>
                  {saving ? 'Saving…' : 'Save note'}
                </Button>
                <Button size="small" variant="contained" color="success" sx={{ flex: 1 }} disabled={saving}
                  onClick={() => patch({ status: 'completed', resolution: resolution.trim() || null })}>
                  Mark complete
                </Button>
              </Stack>
            )}
          </Stack>
        </Box>

        {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
      </Stack>
    </Paper>
  )
}

// The Groups view: name, counts over 7 and 30 days, last seen. A click
// filters the cards to that group.
function GroupsList({ groups, onPick }) {
  if (!groups.length) return <Typography color="text.secondary">No feedback groups yet.</Typography>
  return (
    <Paper variant="outlined" data-testid="feedback-groups">
      <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 70px 70px 190px', columnGap: 2, px: 2, py: 1, borderBottom: 1, borderColor: 'divider' }}>
        {['Group', '7 days', '30 days', 'Last seen'].map((h) => <Typography key={h} sx={LABEL_SX} color="text.secondary">{h}</Typography>)}
      </Box>
      {groups.map((g) => (
        <Box key={g.key} sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 70px 70px 190px', columnGap: 2, px: 2, py: 1, borderBottom: 1, borderColor: 'divider', alignItems: 'center' }}>
          <Link component="button" type="button" underline="hover" onClick={() => onPick(g.key)} sx={{ textAlign: 'left', fontSize: 14, wordBreak: 'break-word' }} data-testid="feedback-group-row">
            {g.name ?? g.key}
          </Link>
          <Typography variant="body2">{g.count7d}</Typography>
          <Typography variant="body2">{g.count30d}</Typography>
          <Typography variant="body2" color="text.secondary">{formatFeedbackDate(g.lastSeen)}</Typography>
        </Box>
      ))}
    </Paper>
  )
}

// The Daily digest (collapsed by default): yesterday's reports by group,
// each with its name, count and the triage of its newest report. Built once
// a day by the feedback sweep; shown here only, never emailed.
function DigestPanel({ onPick }) {
  const [expanded, setExpanded] = useState(false)
  const [digest, setDigest] = useState(null)
  const [state, setState] = useState('idle')

  useEffect(() => {
    if (!expanded || state !== 'idle') return
    setState('loading')
    fetchFeedbackDigest()
      .then((d) => { setDigest(d); setState('done') })
      .catch(() => setState('error'))
  }, [expanded, state])

  return (
    <Accordion expanded={expanded} onChange={(_e, v) => setExpanded(v)} disableGutters variant="outlined" sx={{ mb: 1.5 }} data-testid="feedback-digest">
      <AccordionSummary expandIcon={<ExpandIcon />}>
        <Typography fontWeight={600}>Daily digest</Typography>
        {digest?.date && <Typography color="text.secondary" sx={{ ml: 1 }}>{digest.date}</Typography>}
      </AccordionSummary>
      <AccordionDetails>
        {state === 'loading' && <CircularProgress size={20} />}
        {state === 'error' && <Typography color="text.secondary">The digest could not be loaded.</Typography>}
        {state === 'done' && (!digest?.groups?.length ? (
          <Typography color="text.secondary">{digest?.notBuilt ? 'Yesterday\'s digest has not been built yet.' : 'No feedback yesterday.'}</Typography>
        ) : (
          <Stack spacing={1.25}>
            {digest.groups.map((g) => {
              const view = triageView(g.newest?.triage ? { status: 'ok', ...g.newest.triage } : null)
              return (
                <Box key={g.groupKey} data-testid="feedback-digest-group" sx={{ borderBottom: 1, borderColor: 'divider', pb: 1 }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Link component="button" type="button" underline="hover" onClick={() => onPick(g.groupKey)} sx={{ fontWeight: 600, textAlign: 'left' }}>{g.name ?? g.groupKey}</Link>
                    <Chip size="small" label={g.count} />
                    {g.newest?.convNumber != null && <Typography variant="caption" color="text.secondary">newest #{g.newest.convNumber}</Typography>}
                  </Stack>
                  {view.state === 'ok' ? (
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '150px minmax(0, 1fr)' }, rowGap: 0.25, columnGap: 1.5, mt: 0.5 }}>
                      {view.rows.map((r) => (
                        <Box key={r.key} sx={{ display: 'contents' }}>
                          <Typography variant="body2" color="text.secondary">{r.label}</Typography>
                          <Typography variant="body2">{r.value}</Typography>
                        </Box>
                      ))}
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>No triage for the newest report.</Typography>
                  )}
                </Box>
              )
            })}
          </Stack>
        ))}
      </AccordionDetails>
    </Accordion>
  )
}

export default function FeedbackReview({ onOpenInAgent }) {
  const [items, setItems] = useState([])
  const [users, setUsers] = useState([])
  const [groups, setGroups] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [includeCompleted, setIncludeCompleted] = useState(false)
  const [view, setView] = useState('reports')
  const [filters, setFilters] = useState({ company: '', channel: '', submittedBy: '', assigneeId: '', intent: '', groupKey: '' })
  const [newSort, setNewSort] = useState('priority')
  const [doneSort, setDoneSort] = useState('resolved_desc')

  const setFilter = (key) => (value) => setFilters((f) => ({ ...f, [key]: value }))

  const load = useCallback(() => {
    setLoading(true); setError(null)
    Promise.all([fetchFeedback({ includeCompleted }), fetchFeedbackUsers()])
      .then(([feedback, userList]) => { setItems(feedback); setUsers(userList) })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false))
    // The group names for the Group filter and the Groups view; the page
    // works without them.
    fetchFeedbackGroups().then(setGroups).catch(() => setGroups([]))
  }, [includeCompleted])

  useEffect(() => { load() }, [load])

  function handleUpdated(updated) {
    if (!updated) return
    if (!includeCompleted && updated.status === 'completed') {
      setItems((prev) => prev.filter((i) => i.id !== updated.id))
    } else {
      setItems((prev) => prev.map((i) => (i.id === updated.id ? mergeUpdated(i, updated) : i)))
    }
  }

  async function handleRetryTriage(id) {
    const triage = await retryFeedbackTriage(id)
    setItems((prev) => prev.map((i) => (i.id === id ? { ...i, triage } : i)))
  }

  function pickGroup(groupKey) {
    setFilters((f) => ({ ...f, groupKey }))
    setView('reports')
  }

  const submittedByOptions = useMemo(() => [...new Set(items.map((i) => i.submittedBy).filter(Boolean))].sort(), [items])
  const intentOptions = useMemo(() => [...new Set(items.map((i) => i.intent).filter(Boolean))].sort(), [items])
  const groupChoices = useMemo(() => {
    const opts = groupOptions(items, groups)
    if (filters.groupKey && !opts.some((o) => o.key === filters.groupKey)) {
      const g = groups.find((x) => x.key === filters.groupKey)
      opts.push({ key: filters.groupKey, name: g?.name ?? filters.groupKey })
    }
    return opts
  }, [items, groups, filters.groupKey])

  const filtered = useMemo(() => filterFeedback(items, filters), [items, filters])
  const open = useMemo(() => filtered.filter((i) => i.status === 'open'), [filtered])
  const done = useMemo(() => filtered.filter((i) => i.status === 'completed'), [filtered])
  const sortedOpen = useMemo(() => open.slice().sort(NEW_SORT_COMPARATORS[newSort]), [open, newSort])
  const sortedDone = useMemo(() => done.slice().sort(DONE_SORT_COMPARATORS[doneSort]), [done, doneSort])
  const hasFilters = Object.values(filters).some(Boolean)

  const cardProps = { users, onUpdated: handleUpdated, onOpenInAgent, onRetryTriage: handleRetryTriage, onFilterGroup: pickGroup }

  return (
    <Box sx={{ p: 2 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }} flexWrap="wrap" useFlexGap>
        <Typography variant="h6" fontWeight={700}>Feedback Review</Typography>
        {!loading && (
          <Chip size="small" label={`${open.length} open`} color={open.length > 0 ? 'warning' : 'default'} />
        )}
        <ToggleButtonGroup size="small" exclusive value={view} onChange={(_e, v) => { if (v) setView(v) }} aria-label="View" sx={{ ml: 1 }}>
          <ToggleButton value="reports" sx={{ textTransform: 'none', py: 0.25 }}>Reports</ToggleButton>
          <ToggleButton value="groups" sx={{ textTransform: 'none', py: 0.25 }}>Groups</ToggleButton>
        </ToggleButtonGroup>
        <FormControlLabel
          control={<Switch size="small" checked={includeCompleted} onChange={(e) => setIncludeCompleted(e.target.checked)} />}
          label={<Typography variant="body2">Show completed</Typography>}
          sx={{ ml: 1 }}
        />
        <Button size="small" onClick={load} disabled={loading}>Refresh</Button>
        <Box sx={{ flexGrow: 1 }} />
        <FormControl size="small" sx={{ minWidth: 110 }}>
          <InputLabel>Company</InputLabel>
          <Select value={filters.company} label="Company" onChange={(e) => setFilter('company')(e.target.value)}>
            <MenuItem value="">All companies</MenuItem>
            {KNOWN_COMPANIES.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 110 }}>
          <InputLabel>Channel</InputLabel>
          <Select value={filters.channel} label="Channel" onChange={(e) => setFilter('channel')(e.target.value)}>
            <MenuItem value="">All channels</MenuItem>
            {KNOWN_CHANNELS.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Created By</InputLabel>
          <Select value={filters.submittedBy} label="Created By" onChange={(e) => setFilter('submittedBy')(e.target.value)}>
            <MenuItem value="">All submitters</MenuItem>
            {submittedByOptions.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Assigned To</InputLabel>
          <Select value={filters.assigneeId} label="Assigned To" onChange={(e) => setFilter('assigneeId')(e.target.value)}>
            <MenuItem value="">All assignees</MenuItem>
            <MenuItem value={UNASSIGNED_FILTER_VALUE}><em>Unassigned</em></MenuItem>
            {users.map((u) => <MenuItem key={u.id} value={u.id}>{u.name}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 130 }}>
          <InputLabel>Intent</InputLabel>
          <Select value={filters.intent} label="Intent" onChange={(e) => setFilter('intent')(e.target.value)}>
            <MenuItem value="">All intents</MenuItem>
            {intentOptions.map((i) => <MenuItem key={i} value={i}>{intentLabel(i)}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 160, maxWidth: 260 }}>
          <InputLabel>Group</InputLabel>
          <Select value={filters.groupKey} label="Group" onChange={(e) => setFilter('groupKey')(e.target.value)}>
            <MenuItem value="">All groups</MenuItem>
            {groupChoices.map((g) => <MenuItem key={g.key} value={g.key}>{g.name}</MenuItem>)}
          </Select>
        </FormControl>
        {hasFilters && (
          <Button size="small" variant="text" onClick={() => setFilters({ company: '', channel: '', submittedBy: '', assigneeId: '', intent: '', groupKey: '' })}>
            Clear
          </Button>
        )}
      </Stack>

      <DigestPanel onPick={pickGroup} />

      {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}

      {view === 'groups' ? (
        <GroupsList groups={groups} onPick={pickGroup} />
      ) : loading ? (
        <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress size={28} /></Box>
      ) : items.length === 0 ? (
        <Typography color="text.secondary">No {includeCompleted ? '' : 'open '}feedback items.</Typography>
      ) : filtered.length === 0 ? (
        <Typography color="text.secondary">No feedback items match the current filters.</Typography>
      ) : (
        <Stack spacing={1.5}>
          {open.length > 0 && (
            <Stack direction="row" justifyContent="flex-end">
              <FormControl size="small" sx={{ minWidth: 220 }}>
                <InputLabel>Sort by</InputLabel>
                <Select label="Sort by" value={newSort} onChange={(e) => setNewSort(e.target.value)}>
                  <MenuItem value="priority">Priority</MenuItem>
                  <MenuItem value="created_desc">Created date (newest first)</MenuItem>
                  <MenuItem value="created_asc">Created date (oldest first)</MenuItem>
                </Select>
              </FormControl>
            </Stack>
          )}
          {sortedOpen.map((item) => <FeedbackCard key={item.id} item={item} {...cardProps} />)}

          {includeCompleted && done.length > 0 && (
            <>
              {open.length > 0 && (
                <Divider sx={{ my: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">Completed</Typography>
                </Divider>
              )}
              <Stack direction="row" justifyContent="flex-end">
                <FormControl size="small" sx={{ minWidth: 220 }}>
                  <InputLabel>Sort by</InputLabel>
                  <Select label="Sort by" value={doneSort} onChange={(e) => setDoneSort(e.target.value)}>
                    <MenuItem value="resolved_desc">Resolved date (newest first)</MenuItem>
                    <MenuItem value="resolved_asc">Resolved date (oldest first)</MenuItem>
                    <MenuItem value="priority">Priority</MenuItem>
                  </Select>
                </FormControl>
              </Stack>
              {sortedDone.map((item) => <FeedbackCard key={item.id} item={item} {...cardProps} />)}
            </>
          )}
        </Stack>
      )}
    </Box>
  )
}
